import { useState, useEffect } from 'react'
import api from '../lib/api'
import { TrendingUp, ExternalLink } from 'lucide-react'

interface UtmDetail {
  utmSource: string | null
  utmMedium: string | null
  utmCampaign: string | null
  utmTerm: string | null
  utmContent: string | null
  conversationCount: number
  completedCount: number
  activeCount: number
  bookingCount: number
  conversionRate: string
  bookingConversionRate: string
}

interface UtmAggregate {
  source?: string
  medium?: string
  campaign?: string
  conversations: number
  completed: number
  bookings: number
}

interface UtmData {
  details: UtmDetail[]
  bySource: UtmAggregate[]
  byMedium: UtmAggregate[]
  byCampaign: UtmAggregate[]
}

interface UtmAnalyticsProps {
  timeframe: string
}

export default function UtmAnalytics({ timeframe }: UtmAnalyticsProps) {
  const [utmData, setUtmData] = useState<UtmData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'source' | 'medium' | 'campaign'>('source')

  useEffect(() => {
    loadUtmAnalytics()
  }, [timeframe])

  const loadUtmAnalytics = async () => {
    try {
      setIsLoading(true)
      const response = await api.get(`/api/analytics/utm?timeframe=${timeframe}`)
      if (response.data.success) {
        setUtmData(response.data.utm)
      }
    } catch (error) {
      console.error('Failed to load UTM analytics:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const getActiveData = () => {
    if (!utmData) return []
    switch (activeTab) {
      case 'source':
        return utmData.bySource
      case 'medium':
        return utmData.byMedium
      case 'campaign':
        return utmData.byCampaign
      default:
        return []
    }
  }

  const getLabel = (item: UtmAggregate) => {
    if (activeTab === 'source') return item.source || '(direct)'
    if (activeTab === 'medium') return item.medium || '(none)'
    return item.campaign || '(not set)'
  }

  if (isLoading) {
    return (
      <div className="bg-card p-6 rounded-lg shadow">
        <div className="animate-pulse">
          <div className="h-6 bg-muted rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (!utmData || (utmData.bySource.length === 0 && utmData.byMedium.length === 0 && utmData.byCampaign.length === 0)) {
    return (
      <div className="bg-card p-6 rounded-lg shadow">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-foreground">Traffic Sources</h2>
        </div>
        <div className="text-center py-8 text-muted-foreground">
          <ExternalLink className="w-12 h-12 mx-auto mb-3 text-muted-foreground/40" />
          <p>No UTM tracking data available yet</p>
          <p className="text-sm mt-1">Add UTM parameters to your links to track traffic sources</p>
        </div>
      </div>
    )
  }

  const activeData = getActiveData()
  const maxConversations = Math.max(...activeData.map(item => item.conversations), 1)

  return (
    <div className="bg-card p-6 rounded-lg shadow">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-foreground">Traffic Sources</h2>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('source')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'source'
                ? 'bg-blue-100 text-blue-700'
                : 'text-muted-foreground hover:bg-muted'
            }`}
          >
            Source
          </button>
          <button
            onClick={() => setActiveTab('medium')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'medium'
                ? 'bg-blue-100 text-blue-700'
                : 'text-muted-foreground hover:bg-muted'
            }`}
          >
            Medium
          </button>
          <button
            onClick={() => setActiveTab('campaign')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'campaign'
                ? 'bg-blue-100 text-blue-700'
                : 'text-muted-foreground hover:bg-muted'
            }`}
          >
            Campaign
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {activeData.slice(0, 10).map((item, index) => {
          const label = getLabel(item)
          const percentage = (item.conversations / maxConversations) * 100
          const conversionRate = item.conversations > 0
            ? ((item.completed / item.conversations) * 100).toFixed(1)
            : '0.0'
          const bookingRate = item.conversations > 0
            ? ((item.bookings / item.conversations) * 100).toFixed(1)
            : '0.0'

          return (
            <div key={index} className="group">
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="text-sm font-medium text-foreground truncate">{label}</span>
                  <span className="text-xs text-muted-foreground/60 flex-shrink-0">
                    {item.conversations} chat{item.conversations !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-xs text-muted-foreground">
                    {conversionRate}% completed
                  </span>
                  <span className="text-xs text-green-600 font-medium">
                    {item.bookings} booking{item.bookings !== 1 ? 's' : ''} ({bookingRate}%)
                  </span>
                </div>
              </div>
              <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-colors duration-200"
                  style={{ width: `${percentage}%` }}
                ></div>
              </div>
            </div>
          )
        })}
      </div>

      {activeData.length > 10 && (
        <div className="mt-4 text-center">
          <p className="text-sm text-muted-foreground">
            Showing top 10 of {activeData.length} total {activeTab}s
          </p>
        </div>
      )}
    </div>
  )
}
